import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { RootState } from '../../store';
import { PageHeader } from '../../components/layout';
import financialsService, {
  BudgetSummary,
  CommittedBreakdown,
  CashFlowPoint,
  VarianceRow,
  PaymentSummary,
  CostCodeForecastRow,
} from '../../services/api/financialsService';
import DrawdownsPage from '../drawdowns/DrawdownsPage';

type TabKey = 'overview' | 'cash-flow' | 'variances' | 'payments' | 'drawdowns';

const tabKeys: TabKey[] = ['overview', 'cash-flow', 'variances', 'payments', 'drawdowns'];

export const tabFromSearch = (search: string): TabKey => {
  const requested = new URLSearchParams(search).get('tab');
  return tabKeys.includes(requested as TabKey) ? requested as TabKey : 'overview';
};

export const financialsPathForTab = (tab: TabKey): string => (
  tab === 'overview' ? '/financials' : `/financials?tab=${tab}`
);

const fmtCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-JM', {
    style: 'currency',
    currency: 'JMD',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const fmtPercent = (value: number | undefined | null, digits = 1): string => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(digits)}%`;
};

const fmtDate = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

interface KpiCardProps {
  label: string;
  value: string;
  helper?: string;
  trend?: 'positive' | 'negative' | 'neutral';
}

function KpiCard({ label, value, helper, trend = 'neutral' }: KpiCardProps) {
  const color = trend === 'positive' ? 'success.main' : trend === 'negative' ? 'error.main' : 'text.primary';
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} color={color} sx={{ mt: 0.5 }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewTab({
  budget,
  committed,
  loading,
}: {
  budget: BudgetSummary | null;
  committed: CommittedBreakdown | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!budget) {
    return <Alert severity="info">No budget data for the active project yet.</Alert>;
  }

  const allocated = Number(budget.totalAllocated || 0);
  const spent = Number(budget.totalSpent || 0);
  const commit = Number(committed?.totalCommitted || budget.totalCommitted || 0);
  const remaining = Math.max(allocated - spent - commit, 0);
  const spentPct = allocated > 0 ? (spent / allocated) * 100 : 0;
  const commitPct = allocated > 0 ? (commit / allocated) * 100 : 0;

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Total Allocated" value={fmtCurrency(allocated)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Actual Spent"
            value={fmtCurrency(spent)}
            helper={`${fmtPercent(spentPct, 1)} of budget`}
            trend={spentPct > 100 ? 'negative' : 'neutral'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Committed (POs)"
            value={fmtCurrency(commit)}
            helper={`${fmtPercent(commitPct, 1)} of budget`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Remaining"
            value={fmtCurrency(remaining)}
            trend={remaining > 0 ? 'positive' : 'negative'}
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Budget consumption
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Actual spent</Typography>
            <Typography variant="body2" fontWeight={700}>{fmtPercent(spentPct, 1)}</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(spentPct, 100)}
            sx={{ height: 8, borderRadius: 99 }}
            color={spentPct > 100 ? 'error' : 'primary'}
          />
        </Box>
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Committed via open POs</Typography>
            <Typography variant="body2" fontWeight={700}>{fmtPercent(commitPct, 1)}</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(commitPct, 100)}
            sx={{ height: 8, borderRadius: 99 }}
            color="info"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Committed = sum of approved/sent/confirmed/partial-received PO totals minus accepted GRN value. Distinct from actual spent because committed funds are not yet disbursed.
        </Typography>
      </Paper>

      {budget.overBudgetCount > 0 && (
        <Alert severity="warning">
          {budget.overBudgetCount} budget {budget.overBudgetCount === 1 ? 'line is' : 'lines are'} over allocation. See the Variances tab for detail.
        </Alert>
      )}

      {budget.projectedOverrunCount > 0 && (
        <Alert severity="error">
          {budget.projectedOverrunCount} cost code {budget.projectedOverrunCount === 1 ? 'has' : 'have'} projected EAC above budget.
        </Alert>
      )}

      <CostCodeForecastTable rows={budget.costCodes || []} />
    </Stack>
  );
}

function CostCodeForecastTable({ rows }: { rows: CostCodeForecastRow[] }) {
  if (!rows.length) {
    return <Alert severity="info">No cost-code forecast rows are available for this project.</Alert>;
  }

  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Cost-code forecast
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell align="right">Budget</TableCell>
            <TableCell align="right">Actual</TableCell>
            <TableCell align="right">Committed</TableCell>
            <TableCell align="right">Available</TableCell>
            <TableCell align="right">CTC</TableCell>
            <TableCell align="right">EAC</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.costCodeId} hover>
              <TableCell>{row.code}</TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">{fmtCurrency(row.budget)}</TableCell>
              <TableCell align="right">{fmtCurrency(row.actual)}</TableCell>
              <TableCell align="right">{fmtCurrency(row.committed)}</TableCell>
              <TableCell align="right" sx={{ color: row.available < 0 ? 'error.main' : 'text.primary' }}>
                {fmtCurrency(row.available)}
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                  <Typography variant="body2">{fmtCurrency(row.ctc)}</Typography>
                  {row.ctcOverride !== null && row.ctcOverride !== undefined && (
                    <Chip size="small" label="Override" color="info" variant="outlined" />
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ color: row.projectedOverrun ? 'error.main' : 'text.primary' }}>
                {fmtCurrency(row.eac)}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.projectedOverrun ? `Over ${fmtCurrency(row.overrunAmount)}` : 'On budget'}
                  color={row.projectedOverrun ? 'error' : 'success'}
                  variant={row.projectedOverrun ? 'filled' : 'outlined'}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function CashFlowTab({ data, loading }: { data: CashFlowPoint[]; loading: boolean }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return <Alert severity="info">No cash flow data yet. Drawdown payments will populate this view via the Step 3a financial wiring.</Alert>;
  }

  const totalActual = data.reduce((sum, p) => sum + Number(p.actual || 0), 0);
  const totalProjected = data.reduce((sum, p) => sum + Number(p.projected || 0), 0);
  const varianceTotal = totalActual - totalProjected;

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Actual (period)" value={fmtCurrency(totalActual)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Projected (period)" value={fmtCurrency(totalProjected)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="Variance"
            value={fmtCurrency(varianceTotal)}
            trend={varianceTotal === 0 ? 'neutral' : varianceTotal > 0 ? 'negative' : 'positive'}
            helper={varianceTotal === 0 ? 'on plan' : varianceTotal > 0 ? 'overspend' : 'underspend'}
          />
        </Grid>
      </Grid>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Projected</TableCell>
              <TableCell align="right">Variance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((point, idx) => {
              const v = Number(point.actual || 0) - Number(point.projected || 0);
              return (
                <TableRow key={`${point.month}-${idx}`}>
                  <TableCell>{point.month}</TableCell>
                  <TableCell align="right">{fmtCurrency(Number(point.actual || 0))}</TableCell>
                  <TableCell align="right">{fmtCurrency(Number(point.projected || 0))}</TableCell>
                  <TableCell align="right" sx={{ color: v > 0 ? 'error.main' : v < 0 ? 'success.main' : 'text.primary' }}>
                    {fmtCurrency(v)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

function VariancesTab({ rows, loading }: { rows: VarianceRow[]; loading: boolean }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return <Alert severity="success">No budget variances recorded for this project.</Alert>;
  }

  const significantCount = rows.filter((r) => r.isSignificant).length;

  return (
    <Stack spacing={2}>
      {significantCount > 0 && (
        <Alert severity="warning">
          {significantCount} significant {significantCount === 1 ? 'variance crosses' : 'variances cross'} the project's threshold.
        </Alert>
      )}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Period</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Projected</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Variance</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {fmtDate(row.periodStart)} – {fmtDate(row.periodEnd)}
                </TableCell>
                <TableCell>{row.varianceType.replace(/_/g, ' ')}</TableCell>
                <TableCell>{row.category || '—'}</TableCell>
                <TableCell align="right">{fmtCurrency(Number(row.projectedValue || 0))}</TableCell>
                <TableCell align="right">{fmtCurrency(Number(row.actualValue || 0))}</TableCell>
                <TableCell align="right" sx={{ color: row.varianceAmount > 0 ? 'error.main' : 'success.main' }}>
                  {fmtCurrency(Number(row.varianceAmount || 0))}
                </TableCell>
                <TableCell align="right">{fmtPercent(Number(row.variancePercentage || 0), 2)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.isSignificant ? 'Significant' : row.status.replace(/_/g, ' ')}
                    color={row.isSignificant ? 'warning' : 'default'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

function PaymentsTab({ summary, loading }: { summary: PaymentSummary | null; loading: boolean }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) {
    return <Alert severity="info">No payment data for the active project yet.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="Pending"
            value={fmtCurrency(summary.totalPending)}
            helper={`${summary.pending} ${summary.pending === 1 ? 'payment' : 'payments'}`}
            trend="neutral"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="Approved"
            value={fmtCurrency(summary.totalApproved)}
            helper={`${summary.approved} ${summary.approved === 1 ? 'payment' : 'payments'}`}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="Paid"
            value={fmtCurrency(summary.totalPaid)}
            helper={`${summary.paid} ${summary.paid === 1 ? 'payment' : 'payments'}`}
            trend="positive"
          />
        </Grid>
      </Grid>

      {summary.rows && summary.rows.length > 0 && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Team</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.taskId || '—'}</TableCell>
                  <TableCell>{row.teamId || '—'}</TableCell>
                  <TableCell align="right">{fmtCurrency(Number(row.amount || 0))}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status.replace(/_/g, ' ')} />
                  </TableCell>
                  <TableCell>{fmtDate(row.paidAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

export default function FinancialsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const queryTab = new URLSearchParams(location.search).get('tab');
  const initialTab: TabKey = queryTab === 'drawdowns' ? 'drawdowns' : 'overview';
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [committed, setCommitted] = useState<CommittedBreakdown | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowPoint[]>([]);
  const [variances, setVariances] = useState<VarianceRow[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState<Record<TabKey, boolean>>({
    overview: false,
    'cash-flow': false,
    variances: false,
    payments: false,
    drawdowns: false,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeProjectId) return;
    let cancelled = false;
    const load = async () => {
      setError(null);
      setLoading({ overview: true, 'cash-flow': true, variances: true, payments: true, drawdowns: false });
      try {
        const [bs, cb, cf, vs, ps] = await Promise.allSettled([
          financialsService.getBudgetSummary(activeProjectId),
          financialsService.getCommittedBreakdown(activeProjectId),
          financialsService.getCashFlowComparison(activeProjectId, 12),
          financialsService.listVariances(activeProjectId, {}),
          financialsService.getPaymentSummary(activeProjectId),
        ]);
        if (cancelled) return;
        if (bs.status === 'fulfilled') setBudget(bs.value);
        if (cb.status === 'fulfilled') setCommitted(cb.value);
        if (cf.status === 'fulfilled') setCashFlow(cf.value);
        if (vs.status === 'fulfilled') setVariances(vs.value);
        if (ps.status === 'fulfilled') setPaymentSummary(ps.value);
        const firstError = [bs, cb, cf, vs, ps].find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
        if (firstError) {
          setError(String(firstError.reason?.response?.data?.error || firstError.reason?.message || 'Failed to load financials'));
        }
      } finally {
        if (!cancelled) {
          setLoading({ overview: false, 'cash-flow': false, variances: false, payments: false, drawdowns: false });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeProjectId]);

  const significantVarianceCount = useMemo(
    () => variances.filter((v) => v.isSignificant).length,
    [variances]
  );

  useEffect(() => {
    setTab(tabFromSearch(location.search));
  }, [location.search]);

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Financials"
        subtitle="Budget vs committed vs actual, cash flow, variances, and payments for the active project"
      />

      {!activeProjectId && (
        <Alert severity="warning">Select an active project to load financials.</Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Paper sx={{ borderRadius: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, value: TabKey) => {
            setTab(value);
            navigate(financialsPathForTab(value), { replace: true });
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="cash-flow" label="Cash Flow" />
          <Tab
            value="variances"
            label={
              significantVarianceCount > 0
                ? `Variances (${significantVarianceCount})`
                : 'Variances'
            }
          />
          <Tab value="payments" label="Payments" />
          <Tab value="drawdowns" label="Drawdowns" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 'overview' && (
            <OverviewTab budget={budget} committed={committed} loading={loading.overview} />
          )}
          {tab === 'cash-flow' && <CashFlowTab data={cashFlow} loading={loading['cash-flow']} />}
          {tab === 'variances' && <VariancesTab rows={variances} loading={loading.variances} />}
          {tab === 'payments' && <PaymentsTab summary={paymentSummary} loading={loading.payments} />}
          {tab === 'drawdowns' && <DrawdownsPage embedded />}
        </Box>
      </Paper>
    </Stack>
  );
}
